import api from '@/lib/axios';
import { getAttributionPayload } from '@/lib/utm';
import {
  LoginParams,
  AuthResponse,
  UserInfo,
  CheckEmailParams,
  CheckEmailResponse,
  RegisterParams,
  SendCodeParams,
  ResetPasswordParams,
} from '@/types/auth';

const withAttributionPayload = <TData extends Record<string, unknown>>(data: TData) => ({
  ...getAttributionPayload(),
  ...data,
});

/**
 * 认证模块 API
 * 封装登录、注册及个人信息获取
 */
export const authApi = {
  /**
   * 邮箱密码登录
   * @param data 登录参数 (email, password)
   */
  login: (data: LoginParams): Promise<AuthResponse> => {
    return api.post('/lowcode-business/api/auth/user/email/password/login', data, {
      _skipAuthCheck: true,
    });
  },

  checkEmail: (data: CheckEmailParams): Promise<CheckEmailResponse> => {
    return api.post('/lowcode-business/api/auth/user/checkEmail', data, {
      _skipAuthCheck: true,
    });
  },

  /**
   * 邮箱注册
   * @param data 注册参数 (email, password, description)
   */
  register: (data: RegisterParams): Promise<AuthResponse> => {
    return api.post('/lowcode-business/api/auth/user/emailRegister', withAttributionPayload(data), {
      _skipAuthCheck: true,
    });
  },

  /**
   * 发送注册验证码
   * @param data 发送参数 (email)
   */
  sendRegisterCode: (data: SendCodeParams): Promise<void> => {
    return api.post('/lowcode-business/api/auth/user/sendEmailRegisterCode', data, {
      _skipAuthCheck: true,
    });
  },

  /**
   * 发送重置密码验证码
   * @param data 发送参数 (email)
   */
  sendResetPasswordCode: (data: SendCodeParams): Promise<void> => {
    return api.post('/lowcode-business/api/auth/user/sendEmailCodeForResetPassword', data, {
      _skipAuthCheck: true,
    });
  },

  /**
   * 重置密码
   * @param data 重置参数 (email, code, password)
   */
  resetPassword: (data: ResetPasswordParams): Promise<void> => {
    return api.post('/lowcode-business/api/auth/user/resetPasswordByEmail', data, {
      _skipAuthCheck: true,
    });
  },

  /**
   * 第三方授权：获取 OAuth 跳转地址
   * @param data { authSource: 'GOOGLE', redirectUrl: 授权成功后后端重定向回的前端路由地址 }
   */
  thirdAuthorization: (data: {
    authSource: string;
    redirectUrl?: string;
    timezone?: string;
  }): Promise<{ authorizeUrl: string }> => {
    return api.post('/lowcode-business/api/auth/user/authorize', withAttributionPayload(data), {
      _skipAuthCheck: true,
    });
  },

  /**
   * OAuth 临时码换取真实 token（服务端 OAuth 回调后生成的临时 code）
   * @param data 换取参数 (code)
   */
  exchangeToken: (data: {
    code: string;
  }): Promise<{ userId: number; token: string; newUser: boolean }> => {
    return api.post('/lowcode-business/api/auth/user/exchangeToken', withAttributionPayload(data), {
      _skipAuthCheck: true,
    });
  },

  /**
   * 退出登录
   * @param data 退出登录参数 (email)
   */
  logout: (data: { email: string }): Promise<void> => {
    return api.post('/lowcode-business/api/auth/user/logout', data);
  },

  /**
   * 获取用户信息（token 在请求头中携带，无需请求体参数）
   * silent=true 时业务错误不弹 toast
   */
  getUserInfo: (options?: { silent?: boolean }): Promise<UserInfo> => {
    return api.post('/lowcode-business/api/auth/user/info', undefined, {
      ...(options?.silent ? { _suppressErrorToast: true } : {}),
    });
  },
};
